import React, { useState, useEffect } from 'react';
import { Check, X, Filter, Search, Download } from 'lucide-react';
import * as XLSX from 'xlsx';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:4000/api/v1';

const DireccionEstudiantes = () => {
    const [solicitudes, setSolicitudes] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filtro, setFiltro] = useState('pendiente');
    const [gestion, setGestion] = useState(new Date().getFullYear().toString());
    const [busqueda, setBusqueda] = useState('');
    const [showModal, setShowModal] = useState(false);
    const [selectedSolicitud, setSelectedSolicitud] = useState(null);
    const [observaciones, setObservaciones] = useState('');
    const [actionType, setActionType] = useState('');

    useEffect(() => {
        loadSolicitudes();
    }, [filtro, gestion]);

    const loadSolicitudes = async () => {
        setLoading(true);
        try {
            let url = `${API_URL}/direccion/solicitudes-estudiante?`;
            if (filtro) url += `estado=${filtro}&`;
            if (gestion) url += `gestion=${gestion}`;

            const response = await fetch(url);
            const data = await response.json();
            setSolicitudes(data.solicitudes || []);
        } catch (error) {
            console.error('Error loading solicitudes:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleOpenModal = (sol, action) => {
        setSelectedSolicitud(sol);
        setActionType(action);
        setObservaciones('');
        setShowModal(true);
    };

    const handleAction = async () => {
        if (actionType === 'rechazar' && !observaciones.trim()) {
            alert('Debe ingresar el motivo del rechazo');
            return;
        }

        try {
            const endpoint = actionType === 'aprobar' ? 'aprobar' : 'rechazar';
            const response = await fetch(`${API_URL}/direccion/solicitudes-estudiante/${selectedSolicitud.id_solicitud}/${endpoint}`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ observaciones })
            });

            const data = await response.json();

            if (!response.ok) {
                alert(data.error || 'Error al procesar solicitud');
                return;
            }

            alert(data.message);
            setShowModal(false);
            loadSolicitudes();
        } catch (error) {
            console.error('Error:', error);
            alert('Error al procesar solicitud');
        }
    };

    const handleExportExcel = () => {
        const dataToExport = filteredSolicitudes.map(sol => ({
            'Código': sol.codigo_estudiante || 'N/A',
            'Estudiante': sol.nombre_estudiante || 'N/A',
            'CI': sol.cedula_identidad || 'N/A',
            'Estado': sol.estado,
            'Fecha Solicitud': sol.fecha_solicitud ? new Date(sol.fecha_solicitud).toLocaleDateString() : 'N/A',
            'Motivo': sol.motivo || '',
            'Observaciones': sol.observaciones || ''
        }));

        const ws = XLSX.utils.json_to_sheet(dataToExport);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, 'Solicitudes');
        XLSX.writeFile(wb, `solicitudes_estudiantes_${gestion}.xlsx`);
    };

    const getEstadoColor = (estado) => {
        switch (estado) {
            case 'pendiente': return 'bg-yellow-100 text-yellow-800';
            case 'aprobado': return 'bg-green-100 text-green-800';
            case 'rechazado': return 'bg-red-100 text-red-800';
            case 'consumido': return 'bg-gray-100 text-gray-800';
            default: return 'bg-gray-100 text-gray-800';
        }
    };

    const filteredSolicitudes = solicitudes.filter(sol => {
        if (!busqueda) return true;
        const search = busqueda.toLowerCase();
        return (
            (sol.nombre_estudiante && sol.nombre_estudiante.toLowerCase().includes(search)) ||
            (sol.cedula_identidad && sol.cedula_identidad.toLowerCase().includes(search))
        );
    });

    if (loading) {
        return (
            <div className="p-8 flex justify-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
            </div>
        );
    }

    return (
        <div className="p-6">
            <div className="mb-6">
                <h1 className="text-xl font-bold text-gray-800">Dirección Académica &gt; Autorizaciones &gt; Edición de Estudiantes</h1>
            </div>
            <div className="mb-6 bg-white p-4 rounded-xl shadow-sm border border-gray-100">
                <div className="flex flex-wrap items-center gap-4">
                    {/* BUSCAR Y EXCEL */}
                    <div className="flex-grow flex items-center gap-2">
                        <div className="relative flex-grow">
                            <label className="block text-xs font-semibold text-gray-500 mb-1 ml-1">BUSCAR</label>
                            <div className="relative">
                                <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={16} />
                                <input
                                    type="text"
                                    placeholder="Buscar..."
                                    value={busqueda}
                                    onChange={(e) => setBusqueda(e.target.value)}
                                    className="w-full h-[38px] pl-10 pr-4 border border-gray-300 rounded-full text-sm focus:outline-none focus:ring-2 focus:ring-blue-100 hover:border-blue-500"
                                />
                            </div>
                        </div>
                    </div>
                    {/* GESTIÓN */}
                    <div className="relative">
                        <label className="block text-xs font-semibold text-gray-500 mb-1 ml-1">GESTIÓN</label>
                        <div className="relative">
                            <select
                                value={gestion}
                                onChange={(e) => setGestion(e.target.value)}
                                className="h-[38px] px-4 border border-gray-300 rounded-full bg-white text-sm appearance-none pr-8 hover:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-100 min-w-[100px]"
                            >
                                {[...Array(5)].map((_, i) => {
                                    const year = new Date().getFullYear() - i;
                                    return <option key={year} value={year}>{year}</option>;
                                })}
                            </select>
                            <span className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none text-gray-400 text-xs">▼</span>
                        </div>
                    </div>

                    {/* ESTADO */}
                    <div className="relative">
                        <label className="block text-xs font-semibold text-gray-500 mb-1 ml-1">ESTADO</label>
                        <div className="flex items-center bg-gray-50 p-0.5 border border-gray-200 rounded-full h-[38px]">
                            {['pendiente', 'aprobado', 'rechazado', ''].map((est) => (
                                <button
                                    key={est || 'todos'}
                                    onClick={() => setFiltro(est)}
                                    className={`h-full px-4 rounded-full text-sm font-medium transition flex items-center justify-center ${filtro === est
                                        ? 'bg-blue-900 text-white shadow-sm'
                                        : 'text-gray-700 hover:bg-gray-200'
                                        }`}
                                >
                                    {est ? est.charAt(0).toUpperCase() + est.slice(1) : 'Todos'}
                                </button>
                            ))}
                        </div>
                    </div>
                    <div className="flex flex-col">
                        <label className="block text-xs font-semibold text-transparent mb-1 ml-1 select-none">ACCION</label>
                        <button
                            onClick={handleExportExcel}
                            className="h-[38px] w-[38px] flex items-center justify-center bg-green-600 text-white rounded-full hover:bg-green-700 transition shadow-sm"
                            title="Descargar Excel"
                        >
                            <Download size={18} />
                        </button>
                    </div>

                </div>
            </div>

            {/* Filtros y búsqueda */}
            {/* <div className="bg-white rounded-lg shadow p-4 mb-6">
                <div className="flex flex-wrap items-center gap-4">
                    <div className="flex items-center gap-2">
                        <span className="font-medium text-gray-700">Gestión:</span>
                        <select
                            value={gestion}
                            onChange={(e) => setGestion(e.target.value)}
                            className="border rounded-lg px-3 py-1.5"
                        >
                            {[...Array(5)].map((_, i) => {
                                const year = new Date().getFullYear() - i;
                                return <option key={year} value={year}>{year}</option>;
                            })}
                        </select>
                    </div>
                    <div className="flex items-center gap-2">
                        <Filter size={20} className="text-gray-500" />
                        <span className="font-medium text-gray-700">Estado:</span>
                        <div className="flex gap-2">
                            {['pendiente', 'aprobado', 'rechazado', ''].map((est) => (
                                <button
                                    key={est || 'todos'}
                                    onClick={() => setFiltro(est)}
                                    className={`px-4 py-1 rounded-full text-sm font-medium transition ${filtro === est
                                        ? 'bg-blue-900 text-white'
                                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                                        }`}
                                >
                                    {est || 'Todos'}
                                </button>
                            ))}
                        </div>
                    </div>
                    <div className="flex-1 min-w-[200px]">
                        <div className="relative">
                            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
                            <input
                                type="text"
                                placeholder="Buscar por nombre o CI..."
                                value={busqueda}
                                onChange={(e) => setBusqueda(e.target.value)}
                                className="w-full pl-10 pr-4 py-2 border rounded-lg"
                            />
                        </div>
                    </div>
                    <button
                        onClick={handleExportExcel}
                        className="p-2 bg-green-600 text-white rounded-full hover:bg-green-700 transition shadow-sm"
                        title="Descargar Excel"
                    >
                        <Download size={20} />
                    </button>
                </div>
            </div> */}

            {/* Tabla de solicitudes */}
            <div className="bg-white rounded-lg shadow overflow-hidden">
                <table className="w-full text-sm">
                    <thead className="bg-gray-100">
                        <tr>
                            <th className="px-3 py-3 text-center font-semibold border">Código</th>
                            <th className="px-3 py-3 text-center font-semibold border">Estudiante</th>
                            <th className="px-3 py-3 text-center font-semibold border">CI</th>
                            <th className="px-3 py-3 text-center font-semibold border">Motivo</th>
                            <th className="px-3 py-3 text-center font-semibold border">Fecha</th>
                            <th className="px-3 py-3 text-center font-semibold border">Estado</th>
                            <th className="px-3 py-3 text-center font-semibold border">Motivo del Rechazo</th>
                            {(filtro === 'pendiente' || filtro === '') && (
                                <th className="px-3 py-3 text-center font-semibold border">Acciones</th>
                            )}
                        </tr>
                    </thead>
                    <tbody className="divide-y">
                        {filteredSolicitudes.map((sol) => (
                            <tr key={sol.id_solicitud} className="hover:bg-gray-50">
                                <td className="px-3 py-2 font-medium border">{sol.codigo_estudiante || '-'}</td>
                                <td className="px-3 py-2 border">{sol.nombre_estudiante || 'N/A'}</td>
                                <td className="px-3 py-2 border">{sol.cedula_identidad || 'N/A'}</td>
                                <td className="px-3 py-2 text-sm text-gray-600 border">{sol.motivo || 'Solicitud de edición'}</td>
                                <td className="px-3 py-2 text-sm border">
                                    {new Date(sol.fecha_solicitud).toLocaleDateString()}
                                </td>
                                <td className="px-3 py-2 text-center border">
                                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${getEstadoColor(sol.estado)}`}>
                                        {sol.estado}
                                    </span>
                                </td>
                                <td className={`px-3 py-2 text-center text-sm font-medium border ${sol.estado === 'rechazado' ? 'text-rose-600' : 'text-gray-500 italic'}`}>
                                    {sol.observaciones || '-'}
                                </td>
                                {(filtro === 'pendiente' || filtro === '') && (
                                    <td className="px-3 py-2 text-center border">
                                        {sol.estado === 'pendiente' ? (
                                            <div className="flex gap-2 justify-center">
                                                <button
                                                    onClick={() => handleOpenModal(sol, 'aprobar')}
                                                    className="bg-green-600 text-white px-3 py-1 rounded hover:bg-green-700 text-sm"
                                                    title="Aprobar"
                                                >
                                                    Aprobar
                                                </button>
                                                <button
                                                    onClick={() => handleOpenModal(sol, 'rechazar')}
                                                    className="bg-red-600 text-white px-3 py-1 rounded hover:bg-red-700 text-sm"
                                                    title="Rechazar"
                                                >
                                                    Rechazar
                                                </button>
                                            </div>
                                        ) : (
                                            <span className="text-gray-400 text-sm">-</span>
                                        )}
                                    </td>
                                )}
                            </tr>
                        ))}
                        {filteredSolicitudes.length === 0 && (
                            <tr>
                                <td colSpan={filtro === 'pendiente' || filtro === '' ? "8" : "7"} className="px-4 py-8 text-center text-gray-500">
                                    No hay solicitudes {filtro ? `con estado "${filtro}"` : ''}
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>

            {/* Modal de confirmación */}
            {showModal && selectedSolicitud && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4">
                        <div className="sticky top-0 bg-white border-b flex justify-between items-center mb-4">
                            <h2 className="text-xl font-bold text-blue-900">
                                {actionType === 'aprobar' ? 'Aprobar Solicitud' : 'Rechazar Solicitud'}
                            </h2>
                            <button onClick={() => setShowModal(false)} className="text-gray-400 hover:text-gray-600">
                                <X size={20} />
                            </button>
                        </div>
                        <div className="space-y-2 mb-4 bg-gray-50 p-4 rounded">
                            <p><strong>Estudiante:</strong> {selectedSolicitud.nombre_estudiante}</p>
                            <p><strong>CI:</strong> {selectedSolicitud.cedula_identidad}</p>
                            <p><strong>Motivo:</strong> {selectedSolicitud.motivo || 'Solicitud de edición'}</p>
                        </div>

                        {actionType === 'aprobar' ? (
                            <p className="text-gray-600 mb-4">
                                Al aprobar, Kardex podrá editar los datos de este estudiante <strong>una sola vez</strong>.
                            </p>
                        ) : (
                            <div className="mb-4">
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Motivo del rechazo <span className="text-red-500">*</span>
                                </label>
                                <textarea
                                    value={observaciones}
                                    onChange={(e) => setObservaciones(e.target.value)}
                                    rows="3"
                                    className="w-full border rounded-lg px-3 py-2"
                                    placeholder="Ingrese el motivo del rechazo"
                                />
                            </div>
                        )}

                        <div className="flex justify-end gap-3">
                            <button
                                onClick={() => setShowModal(false)}
                                className="px-4 py-2 border rounded-lg hover:bg-gray-50"
                            >
                                Cancelar
                            </button>
                            <button
                                onClick={handleAction}
                                className={`px-4 py-2 rounded-lg text-white bg-blue-900 text-white rounded-md hover:bg-blue-800 ${actionType === 'aprobar' }`}
                            >
                                {actionType === 'aprobar' ? 'Aprobar' : 'Rechazar'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default DireccionEstudiantes;
